import Alpine from '../../../../../../../CodeQ.Site/Resources/Private/node_modules/alpinejs/dist/module.esm.js';

Alpine.data('docuwareFileViewer', (formSubmitUrl, categoriesUrl = null, options) => ({
    state: 'loading', // loading | loading-long | has-results | no-results | error
    loadingTimeoutId: null,
    latestRequestTime: null,
    searchTerm: '',
    searchType: 'title',
    category: options?.category || '',
    categoryLabels: options?.categoryLabels || [],
    dateFrom: options?.dateFrom ? new Date(options?.dateFrom) : null,
    dateTo: options?.dateTo ? new Date(options?.dateTo) : null,
    categories: [],
    searchResults: {},
    itemsPerPage: options?.itemsPerPage || 50,
    page: 1,
    totalPages: 1,
    paginationItems: [],

    init() {
        const urlParams = new URLSearchParams(window.location.search);

        if (urlParams.has('file-viewer-search-term')) {
            this.searchTerm = urlParams.get('file-viewer-search-term') || '';
        }
        if (urlParams.has('file-viewer-search-category')) {
            this.category = urlParams.get('file-viewer-search-category') || '';
        }
        if (urlParams.has('file-viewer-search-type')) {
            this.searchType = urlParams.get('file-viewer-search-type') || 'fulltext';
        }
        if (urlParams.has('file-viewer-page')) {
            this.page = urlParams.has('file-viewer-page') ? parseInt(urlParams.get('file-viewer-page')) : 1;
        }
        if (urlParams.has('file-viewer-date-from')) {
            this.setDateFrom(urlParams.get('file-viewer-date-from'));
        }
        if (urlParams.has('file-viewer-date-to')) {
            this.setDateTo(urlParams.get('file-viewer-date-to'));
        }

        if (categoriesUrl) {
            fetch(categoriesUrl)
                .then((response) => {
                    if (response.status === 200) {
                        return response.json();
                    } else {
                        throw new Error('The categories could not be loaded.');
                    }
                })
                .then((data) => {
                    this.categories = data;
                    this.category = urlParams.get('file-viewer-search-category') || '';
                })
                .catch(() => {
                    this.setErrorState();
                })
        }

        this.loadSearchResults();
        this.addArrows();
        this.updateCurrentUrlWhenFormChanges();
    },

    triggerSearch() {
        if (this.page === 1) {
            this.loadSearchResults();
        } else {
            this.page = 1;
        }
    },

    paginate(newPage) {
        this.page = newPage;
    },

    hasMorePages() {
        return this.totalPages > this.page;
    },

    hasLessPages() {
        return this.page > 1;
    },

    setDateFrom(newDateFrom) {
        if (!newDateFrom) {
            this.dateFrom = null;
            return;
        }

        try {
            this.dateFrom = new Date(Date.parse(newDateFrom + 'T00:00:00Z')).toISOString();
        } catch (e) {
            console.error(e);
            this.dateFrom = new Date(new Date().setFullYear(new Date().getFullYear() - 10)).toISOString();
        }
    },

    setDateTo(newDateTo) {
        if (!newDateTo) {
            this.dateTo = null;
            return;
        }

        try {
            this.dateTo = new Date(Date.parse(newDateTo + 'T23:59:59Z')).toISOString();
        } catch (e) {
            console.error(e);
            this.dateTo = new Date().toISOString();
        }
    },

    loadSearchResults(e) {
        if (e) e.preventDefault();
        this.state = 'loading';

        const requestTime = Date.now();
        this.latestRequestTime = requestTime;

        if (this.loadingTimeoutId) {
            clearTimeout(this.loadingTimeoutId);
            this.loadingTimeoutId = null;
        }

        this.loadingTimeoutId = setTimeout(() => {
            if (this.state === 'loading' && this.latestRequestTime === requestTime) {
                this.state = 'loading-long';
            }
        }, 10000);

        fetch(formSubmitUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                searchTerm: this.searchTerm,
                searchType: this.searchType,
                category: this.category,
                dateFrom: this.dateFrom,
                dateTo: this.dateTo,
                start: (this.page - 1) * this.itemsPerPage,
                count: this.itemsPerPage,
            })
        })
            .then((response) => {
                if (response.status === 200) {
                    return response.json();
                } else {
                    throw new Error('The file results could not be loaded.');
                }
            })
            .then((data) => {
                if (this.latestRequestTime === requestTime) {
                    this.searchResults = data;
                    this.totalPages = Math.ceil(this.searchResults.total / this.itemsPerPage);
                    this.paginationItems = this.getSearchResultsPaginationItems(this.page, this.totalPages);
                    this.state = this.searchResults.total > 0 ? 'has-results' : 'no-results';
                }
            })
            .catch(() => {
                if (this.latestRequestTime === requestTime) {
                    this.setErrorState();
                }
            })
            .finally(() => {
                if (this.loadingTimeoutId) {
                    clearTimeout(this.loadingTimeoutId);
                    this.loadingTimeoutId = null;
                }
            })
    },

    clearFiltersAndLoadSearchResults() {
        this.page = 1;
        this.searchTerm = '';
        this.searchType = '';
        this.category = '';
        this.loadSearchResults();
    },

    setErrorState() {
        this.state = 'error';
        this.searchResults = {};
        this.totalPages = 1;
        this.paginationItems = [];

        if (this.loadingTimeoutId) {
            clearTimeout(this.loadingTimeoutId);
            this.loadingTimeoutId = null;
        }
    },

    updateCurrentUrlWhenFormChanges() {
        const updateUrl = (key, value) => {
            const url = new URL(window.location.href);
            const params = url.searchParams;

            if (value === null) {
                params.delete(key);
            } else {
                params.set(key, value);
            }

            url.search = params.toString();
            window.history.replaceState({}, '', url.toString());
        };

        this.$watch('searchTerm', (value) => {
            updateUrl('file-viewer-search-term', value);
            this.triggerSearch();
        });
        this.$watch('searchType', (value) => {
            updateUrl('file-viewer-search-type', value);
            this.triggerSearch();
        });
        this.$watch('dateFrom', (value) => {
            updateUrl('file-viewer-date-from', value ? value.split('T')[0] + '' : null);
            this.triggerSearch();
        });
        this.$watch('dateTo', (value) => {
            updateUrl('file-viewer-date-to', value ? value?.split('T')[0] + '' : null);
            this.triggerSearch();
        });
        this.$watch('category', (value) => {
            updateUrl('file-viewer-search-category', value);
            this.triggerSearch();
        });
        this.$watch('page', (value) => {
            updateUrl('file-viewer-page', value);
            this.loadSearchResults();
        });
    },

    onSearchTermEnter($el) {
        let isMobile = window.innerWidth < 768;
        if (isMobile) {
            $el.blur();
            this.$root.querySelector('.docuware-file-viewer__results')?.scrollIntoView({behavior: 'smooth', block: 'start'});
        }
    },

    addArrows() {
        let figureWrapper = this.$root.querySelector('.docuware-file-viewer__figure-wrapper');
        let figure = this.$root.querySelector('.docuware-file-viewer__figure');
        if (!figure) return;

        // There is also DocumentFileViewer.List which does not use a table layout
        let scrollButtons = document.createElement('DIV');
        scrollButtons.classList.add('container__scroll-buttons');
        scrollButtons.innerHTML = `
            <button class="container__scroll-button container__scroll-button--left" id="scroll-left" aria-label="Scroll to left" title="Scroll left">
                Scroll left
            </button>
            <button class="container__scroll-button container__scroll-button--right container__scroll-button--is-available" id="scroll-right" aria-label="Scroll to right" title="Scroll right">
                Scroll right
            </button>`
        let scrollButtonsArray = Array.from(scrollButtons.querySelectorAll('button'));

        figureWrapper.append(scrollButtons);
        let buttonLeft = scrollButtons.querySelector('.container__scroll-button--left');
        let buttonRight = scrollButtons.querySelector('.container__scroll-button--right');

        scrollButtonsArray.forEach(element => {
            element.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();

                let type = 'left';
                if (e.target.classList.contains('container__scroll-button--right')) {
                    type = 'right';
                }

                figure.scrollTo({top: 0, left: figure.scrollLeft + window.innerWidth * (type === 'left' ? -1 : 1), behavior: 'smooth'});
            });
        });

        window.addEventListener('scroll', (e) => {
            if (!e.target.classList || !e.target.classList.contains('docuware-file-viewer__figure')) return;

            //buttons
            let containerRect = figure.getBoundingClientRect();
            let isEdgeLeft = figure.scrollLeft <= 10;
            let isEdgeRight = figure.scrollWidth - figure.scrollLeft - containerRect.width <= 10

            buttonLeft.classList[isEdgeLeft ? 'remove' : 'add']('container__scroll-button--is-available');
            buttonRight.classList[isEdgeRight ? 'remove' : 'add']('container__scroll-button--is-available');
        }, true);
    },

    getSearchResultsPaginationItems(currentPage, totalPages) {
        const total = Math.max(1, Number(totalPages) || 1);
        const current = Math.min(Math.max(1, Number(currentPage) || 1), total);
        const maxLinks = 8; // window size including first/last when applicable
        const windowSize = Math.max(3, Math.min(maxLinks, total));

        const items = [];
        const addPage = (p) => items.push({type: 'page', value: p});
        const addDots = (position) => items.push({type: 'dots', position});

        if (total <= windowSize) {
            for (let i = 1; i <= total; i++) addPage(i);
        } else {
            addPage(1);
            const innerCount = windowSize - 2;
            let start = Math.max(2, current - Math.floor(innerCount / 2));
            let end = Math.min(total - 1, start + innerCount - 1);
            start = Math.max(2, Math.min(start, end - innerCount + 1));
            if (start > 2) addDots('left');
            for (let i = start; i <= end; i++) addPage(i);
            if (end < total - 1) addDots('right');
            addPage(total);
        }
        return items;
    },

    updateCategory(newCategoryValue) {
        this.category = newCategoryValue;
    }
}));
